import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import WelcomeBackPage from "@/pages/project-v2/WelcomeBackPage";
import ProjectDetailsV2Page from "@/pages/project-v2/ProjectDetailsV2Page";
import { CollaboratorRoleSelection } from "@/components/CollaboratorRoleSelection";

interface ProjectV2RouterProps {
  // Optional prop to force showing the welcome flow (for testing)
  forceWelcomeFlow?: boolean;
}

const ProjectV2Router: React.FC<ProjectV2RouterProps> = ({ forceWelcomeFlow = false }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [hasSeenWelcomeFlow, setHasSeenWelcomeFlow] = useState<boolean | null>(null);
  const [showRoleSelection, setShowRoleSelection] = useState(false);

  // Fetch project data to check if it's reviewed and ready
  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId || 'no-id'],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is missing");
      }
      return apiClient.getProject(projectId);
    },
    enabled: !!projectId,
  });

  // Check localStorage for whether user has seen the welcome flow for this project
  useEffect(() => {
    if (projectId) {
      const welcomeFlowKey = `welcome-flow-seen-${projectId}`;
      const hasSeenFlow = localStorage.getItem(welcomeFlowKey) === 'true';
      setHasSeenWelcomeFlow(hasSeenFlow);
    }
  }, [projectId]);

  // Handle navigation logic
  useEffect(() => {
    if (!project || hasSeenWelcomeFlow === null || isLoading || !user) {
      return; // Still loading or determining state
    }

    // Check if current user is a collaborator (not the project owner)
    const isOwner = project.user_id === user._id;
    const currentUserCollaborator = project.collaborators?.find(c => c.user_id === user._id) as any;
    const isCollaborator = !isOwner && !!currentUserCollaborator;

    // Add debugging logs for role selection tracking
    console.log("🐛 [DEBUG] ProjectV2Router - Role selection check:", {
      projectId: project._id,
      userId: user._id,
      userEmail: user.email,
      isOwner,
      isCollaborator,
      collaboratorData: currentUserCollaborator,
      collaboratorRoles: currentUserCollaborator?.roles || [],
      hasRoles: currentUserCollaborator?.roles && currentUserCollaborator.roles.length > 0
    });

    // Check if project is reviewed and ready for the flow
    const isProjectReviewed = project.is_reviewed && project.status !== "pending";
    
    if (!isProjectReviewed) {
      console.log("🐛 [DEBUG] ProjectV2Router - Project not reviewed, redirecting to initial assessment");
      // Project not yet reviewed, redirect to initial assessment
      navigate(`/initial-assessment?projectId=${project._id}`);
      return;
    }

    // COLLABORATOR LOGIC: Check if collaborator has completed role selection
    if (isCollaborator) {
      const hasCompletedRoleSelection = currentUserCollaborator?.roles && currentUserCollaborator.roles.length > 0;
      
      if (!hasCompletedRoleSelection) {
        console.log("🐛 [DEBUG] ProjectV2Router - Collaborator has not completed role selection, showing role selection");
        setShowRoleSelection(true);
        return;
      } else {
        console.log("🐛 [DEBUG] ProjectV2Router - Collaborator has completed role selection, proceeding to project details");
      }
      
      navigate(`/project-v2/project-details-v2/${projectId}`, { replace: true });
      return;
    }

    // OWNER LOGIC: If user has already seen the welcome flow and we're not forcing it, go directly to final page
    if (hasSeenWelcomeFlow && !forceWelcomeFlow) {
      navigate(`/project-v2/project-details-v2/${projectId}`, { replace: true });
      return;
    }

    // If this is the first time or we're forcing the flow, show welcome page
    // The welcome flow will handle marking it as seen when user completes it
  }, [project, hasSeenWelcomeFlow, isLoading, navigate, projectId, forceWelcomeFlow, user]);

  // Handle role selection completion
  const handleRolesUpdated = async (roles: string[]) => {
    console.log("🐛 [DEBUG] ProjectV2Router - Roles updated:", roles);
    // Refresh project data to get updated collaborator info
    await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    setShowRoleSelection(false);
    // Navigate to project details after role selection
    navigate(`/project-v2/project-details-v2/${projectId}`, { replace: true });
  };

  const handleRoleSelectionCancel = () => {
    console.log("🐛 [DEBUG] ProjectV2Router - Role selection cancelled, redirecting to dashboard");
    navigate('/dashboard');
  };

  // Show loading state while determining what to show
  if (isLoading || hasSeenWelcomeFlow === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Show role selection for collaborators who haven't completed it
  if (showRoleSelection && projectId) {
    const currentUserCollaborator = project?.collaborators?.find(c => c.user_id === user?._id) as any;
    return (
      <CollaboratorRoleSelection
        projectId={projectId}
        currentRoles={currentUserCollaborator?.roles || []}
        onRolesUpdated={handleRolesUpdated}
        onCancel={handleRoleSelectionCancel}
      />
    );
  }

  // If we reach here, show the welcome page (first time flow)
  return <WelcomeBackPage />;
};

export default ProjectV2Router;